// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelChart
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS funnel visualization for a shared funnel
 * analysis result. The component presents each step's conversion bar plus the
 * overall conversion summary for onboarding and checkout flows.
 */

import type { FunnelResult } from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS funnel chart.
 *
 * @property result Funnel analysis result to visualize.
 * @property isLoading Whether the chart should render placeholder steps.
 * @property className Optional class name merged into the chart wrapper.
 */
export interface FunnelChartProps {
  result: FunnelResult | null;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React CSS funnel chart for a shared funnel result.
 *
 * @param props Funnel result payload for the chart.
 * @returns A funnel chart element, or an empty-state element when no steps exist.
 */
export function FunnelChart(props: FunnelChartProps): React.ReactElement {
  const copy = useAnalyticsCopy();

  if (props.error) {
    return (
      <Card className={props.className}>
        <CardHeader>
          <CardTitle className="gn-analytics-funnel__title">
            {props.result?.name ?? copy.t("analytics.funnel.defaultName")}
          </CardTitle>
        </CardHeader>
        <CardContent className="gn-analytics-funnel">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (props.isLoading) {
    return (
      <Card className={props.className}>
        <CardHeader>
          <CardTitle className="gn-analytics-funnel__title">
            {props.result?.name ?? copy.t("analytics.funnel.defaultName")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="gn-analytics-funnel gn-analytics-funnel--loading"
            data-analytics-funnel-loading
            aria-busy="true"
          >
            {Array.from(
              { length: 4 },
              (_, index) => `funnel-skeleton-${index}`,
            ).map((skeletonId) => (
              <div
                key={skeletonId}
                className="gn-analytics-funnel__step gn-analytics-funnel__step--loading"
              >
                <Skeleton height={18} />
                <Skeleton height={10} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!props.result || props.result.steps.length === 0) {
    return (
      <Card className={props.className}>
        <CardContent>
          <EmptyState title={copy.t("analytics.funnel.empty")} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={props.className}>
      <CardHeader>
        <CardTitle className="gn-analytics-funnel__title">
          {props.result.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="gn-analytics-funnel">
        <div className="gn-analytics-funnel__steps">
          {props.result.steps.map((step, index) => (
            <div key={step.name} className="gn-analytics-funnel__step">
              <div className="gn-analytics-funnel__step-header">
                <span>{step.name}</span>
                <Badge variant="secondary">{step.count}</Badge>
              </div>
              <Progress value={step.conversionRate} />
              {index > 0 && (
                <span className="gn-analytics-funnel__dropoff">
                  {copy.t("analytics.funnel.dropoff", {
                    percentage: step.dropoffRate.toFixed(1),
                  })}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="gn-analytics-funnel__summary">
          <span>
            {copy.t("analytics.funnel.overall", {
              percentage: props.result.overallConversionRate.toFixed(1),
            })}
          </span>
          <span>
            {props.result.totalConversions}/{props.result.totalEntries}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
